import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { recordPartnerSitePromoClick } from "@/api/public.api.js";
import "./partner-site-promo-card.css";

/**
 * Renders text only when it fits on a single line in the current card width.
 * If it would wrap, the element is omitted entirely.
 */
function OneLineOrHide({ text, className }) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(Boolean(text));

    useLayoutEffect(() => {
        if (!text) {
            setVisible(false);
            return undefined;
        }

        const el = ref.current;
        if (!el) {
            return undefined;
        }

        const measure = () => {
            setVisible(el.scrollWidth <= el.clientWidth + 1);
        };

        measure();
        const observer = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(measure)
            : null;
        observer?.observe(el);
        return () => observer?.disconnect();
    }, [text]);

    if (!text) {
        return null;
    }

    // Keep measuring while hidden so a wider layout can bring it back.
    return (
        <span
            ref={ref}
            className={`${className}${visible ? "" : " is-hidden"}`}
            aria-hidden={!visible}
        >
            {text}
        </span>
    );
}

/**
 * Catalogue card promoting an external partner / sister website (Publicité + image slideshow).
 */
export default function PartnerSitePromoCard({ promo }) {
    const images = promo?.imageUrls?.filter(Boolean) || [];
    const [index, setIndex] = useState(0);

    useEffect(() => {
        setIndex(0);
    }, [images.join("|")]);

    useEffect(() => {
        if (images.length < 2) {
            return undefined;
        }
        const reduceMotion = typeof window !== "undefined"
            && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduceMotion) {
            return undefined;
        }
        const intervalMs = Math.max(1000, Number(promo.intervalMs) || 5000);
        const timer = window.setInterval(() => {
            setIndex((current) => (current + 1) % images.length);
        }, intervalMs);
        return () => window.clearInterval(timer);
    }, [images, promo?.intervalMs]);

    if (!promo?.url || !promo?.id || images.length === 0) {
        return null;
    }

    const title = promo.title || "Découvrir";
    const subtitle = (promo.subtitle || "").trim();
    const description = promo.description?.trim() || "";

    const handleClick = (event) => {
        event.preventDefault();
        // Open immediately so tracking never delays or blocks the partner site (and avoids popup blockers).
        window.open(promo.url, "_blank", "noopener,noreferrer");
        recordPartnerSitePromoClick(promo.id || promo.code);
    };

    return (
        <a
            href={promo.url}
            className="product-card card partner-site-promo-card"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${title} — Publicité`}
            onClick={handleClick}
        >
            <div className="product-image partner-site-promo-image">
                <span className="partner-site-promo-badge">Publicité</span>
                {images.map((src, i) => (
                    <img
                        key={src}
                        src={src}
                        alt=""
                        className={i === index ? "partner-site-promo-slide is-active" : "partner-site-promo-slide"}
                        loading={i === 0 ? "eager" : "lazy"}
                        decoding="async"
                    />
                ))}
            </div>
            <div className="product-body partner-site-promo-body">
                <OneLineOrHide
                    text={subtitle}
                    className="product-category partner-site-promo-subtitle"
                />
                <h3 className="product-title partner-site-promo-title">{title}</h3>
                {description && (
                    <p className="partner-site-promo-description">{description}</p>
                )}
                <div className="product-footer partner-site-promo-footer">
                    <span className="partner-site-promo-cta">Voir le site</span>
                </div>
            </div>
        </a>
    );
}
